import React, { useMemo } from "react";
import { Order } from "../types";

export type ResolveActionType =
	| "New driver assigned"
	| "Kitchen remaking"
	| "Moved to the front of the que"
	| "Order Delivered"
	| "Order Cancelled";

type TaggedOrder = Order & {
	ticketState?: string;
	agentName?: string;
};

type Props = {
	orders: TaggedOrder[];
	onTakeAction?: (orderId: string, actionType: ResolveActionType) => void;
};

type Level = "critical" | "medium" | "low";

function riskPercentValue(value: unknown): number | null {
	if (value === null || value === undefined) return null;
	const raw = value.toString().trim().replace("%", "");
	if (raw === "") return null;
	const n = Number(raw);
	if (Number.isNaN(n)) return null;
	return n > 0 && n <= 1 ? n * 100 : n;
}

function formatRiskPercent(value: unknown): string {
	const pct = riskPercentValue(value);
	return pct === null ? "—" : `${Math.round(pct)}%`;
}

function riskLevel(risk: string, riskPercent?: unknown): Level {
	const pct = riskPercentValue(riskPercent);
	if (pct !== null) {
		if (pct >= 70) return "critical";
		if (pct >= 40) return "medium";
		return "low";
	}
	const r = (risk || "").toLowerCase();
	if (r.indexOf("critical") !== -1 || r.indexOf("high") !== -1) return "critical";
	if (r.indexOf("med") !== -1) return "medium";
	return "low";
}

function percentColour(value: unknown): string {
	const pct = riskPercentValue(value);
	if (pct === null) return "var(--ct-ink)";
	if (pct >= 70) return "var(--ct-crit)";
	if (pct >= 40) return "var(--ct-warn)";
	return "var(--ct-ink)";
}

function StatusPill({ status, level }: { status: string; level: Level }) {
	if (level === "low") {
		return (
			<span style={{ fontSize: 13, fontWeight: 500, color: "var(--ct-ink)", whiteSpace: "nowrap" }}>
				{status}
			</span>
		);
	}
	const tone =
		level === "critical"
			? { bg: "var(--ct-crit)", fg: "#ffffff" }
			: { bg: "var(--ct-warn-bg)", fg: "var(--ct-warn)" };

	return (
		<span
			style={{
				display: "inline-block",
				padding: "5px 11px",
				borderRadius: 20,
				fontSize: 12,
				fontWeight: 600,
				color: tone.fg,
				background: tone.bg,
				whiteSpace: "nowrap",
			}}
		>
			{status}
		</span>
	);
}

function TicketPill({ state }: { state: string }) {
	const isNew = state === "Breached (New)";
	return (
		<span
			style={{
				display: "inline-block",
				padding: "4px 10px",
				borderRadius: 20,
				fontSize: 11.5,
				fontWeight: 600,
				whiteSpace: "nowrap",
				color: isNew ? "var(--ct-crit)" : "var(--ct-warn)",
				background: isNew ? "var(--ct-crit-bg)" : "var(--ct-warn-bg)",
			}}
		>
			{state}
		</span>
	);
}

const COLUMNS = [
	"Order", "Age", "Risk %", "Ticket", "Operator",
	"Customer", "Brand", "Location",
	"Status", "Root cause",
];

const LEVEL_RANK: Record<Level, number> = { critical: 0, medium: 1, low: 2 };

export function OrdersTable({ orders }: Props) {
	const sorted = useMemo(() => {
		return [...orders].sort((a, b) => {
			const byLevel =
				LEVEL_RANK[riskLevel(a.risk, a.riskPercent)] -
				LEVEL_RANK[riskLevel(b.risk, b.riskPercent)];
			if (byLevel !== 0) return byLevel;
			return (Number(b.age) || 0) - (Number(a.age) || 0);
		});
	}, [orders]);

	return (
		<div
			style={{
				borderRadius: 10,
				border: "1px solid var(--ct-line)",
				background: "var(--ct-surface)",
				overflow: "hidden",
				width: "100%",
			}}
		>
			<div style={{ overflowX: "auto", width: "100%" }}>
				<table
					style={{
						width: "100%",
						minWidth: "1200px",
						borderCollapse: "collapse",
						fontSize: 13,
					}}
				>
					<thead>
						<tr style={{ background: "var(--ct-surface-2)" }}>
							{COLUMNS.map((h, i) => (
								<th
									key={h}
									style={{
										padding: "11px 16px",
										paddingLeft: i === 0 ? 24 : 16,
										textAlign: "left",
										fontSize: 12,
										fontWeight: 500,
										color: "var(--ct-ink-3)",
										borderBottom: "1px solid var(--ct-line)",
										whiteSpace: "nowrap",
									}}
								>
									{h}
								</th>
							))}
						</tr>
					</thead>

					<tbody>
						{sorted.map((o, idx) => {
							const level = riskLevel(o.risk, o.riskPercent);
							const edge =
								level === "critical"
									? "var(--ct-crit)"
									: level === "medium"
										? "var(--ct-warn)"
										: "var(--ct-ok)";

							return (
								<tr
									key={o.id}
									style={{
										borderBottom:
											idx === sorted.length - 1
												? "none"
												: "1px solid var(--ct-line-soft)",
										transition: "background .12s ease",
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.background = "var(--ct-row-hover)";
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.background = "transparent";
									}}
								>
									{/* Order */}
									<td
										style={{
											padding: "16px",
											paddingLeft: 24,
											whiteSpace: "nowrap",
											position: "relative",
										}}
									>
										<span
											style={{
												position: "absolute",
												left: 0,
												top: 0,
												bottom: 0,
												width: 6,
												background: edge,
											}}
										/>
										<span
											title={o.id}
											className="ct-num"
											style={{
												fontWeight: 600,
												fontSize: 13,
												color: "var(--ct-accent)",
											}}
										>
											{o.id.length > 8 ? o.id.slice(0, 8) + "…" : o.id}
										</span>
									</td>

									{/* Age */}
									<td
										className="ct-num"
										style={{
											padding: "16px",
											whiteSpace: "nowrap",
											color: level === "critical" ? "var(--ct-crit)" : "var(--ct-ink)",
											fontWeight: level === "critical" ? 600 : 500,
											fontSize: level === "critical" ? 16 : 14,
										}}
									>
										{o.age}
									</td>

									{/* Risk % */}
									<td
										className="ct-num"
										style={{
											padding: "16px",
											whiteSpace: "nowrap",
											color: percentColour(o.riskPercent),
											fontWeight: 600,
											fontSize: 14,
										}}
									>
										{formatRiskPercent(o.riskPercent)}
									</td>

									{/* Ticket */}
									<td style={{ padding: "16px", whiteSpace: "nowrap" }}>
										<TicketPill state={(o as TaggedOrder).ticketState || "Breached (New)"} />
									</td>

									{/* Operator */}
									<td
										style={{
											padding: "16px",
											whiteSpace: "nowrap",
											fontSize: 13,
											fontWeight: 500,
											color: (o as TaggedOrder).agentName
												? "var(--ct-ink)"
												: "var(--ct-ink-3)",
										}}
									>
										{(o as TaggedOrder).agentName || "—"}
									</td>

									{/* Customer */}
									<td
										style={{
											padding: "16px",
											color: "var(--ct-ink)",
											fontWeight: 600,
											fontSize: 14,
											whiteSpace: "nowrap",
										}}
									>
										{o.customer}
									</td>

									{/* Brand */}
									<td
										style={{
											padding: "16px",
											color: "var(--ct-ink)",
											fontWeight: 500,
											whiteSpace: "nowrap",
										}}
									>
										{o.brand}
									</td>

									{/* Location */}
									<td style={{ padding: "16px", color: "var(--ct-ink-2)", whiteSpace: "nowrap" }}>
										{o.location}
									</td>

									{/* Status */}
									<td style={{ padding: "16px", whiteSpace: "nowrap" }}>
										<StatusPill status={o.status} level={level} />
									</td>

									{/* Root cause */}
									<td
										title={o.rootCauseDelayAnalysis}
										style={{
											padding: "16px",
											color: "var(--ct-ink-2)",
											fontSize: 13,
											minWidth: 240,
											maxWidth: 380,
											whiteSpace: "normal",
											wordBreak: "break-word",
											lineHeight: 1.45,
										}}
									>
										{o.rootCauseDelayAnalysis}
									</td>
								</tr>
							);
						})}

						{sorted.length === 0 && (
							<tr>
								<td
									colSpan={COLUMNS.length}
									style={{
										textAlign: "center",
										padding: "56px 16px",
										fontSize: 14,
										color: "var(--ct-ink-3)",
									}}
								>
									Nothing needs attention right now.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
}
